import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Turnstile from "react-turnstile";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Field } from "@/components/Form";
import { Eyebrow, Headline } from "@/components/Editorial";
import ProgressBar from "@/components/ProgressBar";
import { useCapture } from "@/lib/capture";
import { apiPost } from "@/lib/api";
import { titleCase } from "@/lib/format";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const DISPOSABLE = ["mailinator.com", "tempmail.com", "guerrillamail.com", "10minutemail.com", "yopmail.com", "trashmail.com"];

export default function GetAccess() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const refFromUrl = search.get("ref");
  const nextFromUrl = search.get("next");
  const { data, update } = useCapture();

  // Pre-fill ref from URL once
  useEffect(() => {
    if (refFromUrl && !data.referralCode) update({ referralCode: refFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refFromUrl]);

  // Device fingerprint
  useEffect(() => {
    (async () => {
      try {
        const fp = await FingerprintJS.load();
        const r = await fp.get();
        update({ deviceFingerprint: r.visitorId });
      } catch {
        // non-blocking
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [referralStatus, setReferralStatus] = useState(null);
  useEffect(() => {
    if (!data.referralCode) {
      setReferralStatus(null);
      return;
    }
    apiPost("/consumer/referrals/validate", { code: data.referralCode }, { auth: false })
      .then((r) => setReferralStatus(r))
      .catch(() => setReferralStatus({ valid: false }));
  }, [data.referralCode]);

  const validate = () => {
    if (!data.firstName || data.firstName.trim().length < 2) return "Please enter your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Please enter a valid email.";
    const domain = data.email.split("@")[1]?.toLowerCase();
    if (DISPOSABLE.includes(domain)) return "Please use a real email address.";
    if (!/^\d{5}$/.test(data.zip)) return "Please enter a 5-digit ZIP.";
    if (!data.password || data.password.length < 8) return "Password must be at least 8 characters.";
    if (!data.turnstileToken) return "Please complete the human verification.";
    return null;
  };

  const submit = async () => {
    // Defensive: titleCase names one more time before continuing
    update({ firstName: titleCase(data.firstName), lastName: titleCase(data.lastName) });
    const err = validate();
    if (err) {
      toast(err, "error");
      return;
    }
    // Don't sign up here — keep flow: email/zip captured, move to phone offer step.
    // Preserve `next` so the final auth completion can redirect back
    // to /research (or wherever the user came from).
    navigate(`/phone-offer${nextFromUrl ? `?next=${encodeURIComponent(nextFromUrl)}` : ""}`);
  };

  return (
    <div data-testid="get-access-page" style={{ minHeight: "100vh", background: C.ink, color: C.cream }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px" }}>
        <Link to="/"><Logo light size={22} /></Link>
        <Link to="/" data-testid="capture-close" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.5)", textDecoration: "none" }}>
          CLOSE ✕
        </Link>
      </header>

      <ProgressBar current={1} total={3} />

      <div style={{ maxWidth: 540, margin: "0 auto", padding: "48px 24px 96px" }}>
        <Eyebrow color={C.peach}>★ STEP 01 / 03</Eyebrow>
        <Headline before="LET'S START" italic="with the basics." color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
        <p style={{ marginTop: 18, color: "rgba(250,246,240,0.7)", fontSize: 16, lineHeight: 1.6 }}>
          Just a few details so we can route your application to the right physician.
        </p>

        <div style={{ background: C.cream, color: C.ink, marginTop: 32, padding: 32 }}>
          <Field label="FIRST NAME" name="given-name" id="capture-firstname" autoComplete="given-name" value={data.firstName} onChange={(v) => update({ firstName: v })} onBlur={() => update({ firstName: titleCase(data.firstName) })} placeholder="Jordan" testId="capture-firstname" autoFocus required />
          <Field label="LAST NAME" name="family-name" id="capture-lastname" autoComplete="family-name" value={data.lastName} onChange={(v) => update({ lastName: v })} onBlur={() => update({ lastName: titleCase(data.lastName) })} placeholder="Lee" testId="capture-lastname" required />
          <Field label="EMAIL ADDRESS" type="email" name="email" id="capture-email" autoComplete="email" value={data.email} onChange={(v) => update({ email: v })} placeholder="you@email.com" testId="capture-email" required />
          <Field label="ZIP CODE" name="postal-code" id="capture-zip" autoComplete="postal-code" inputMode="numeric" value={data.zip} onChange={(v) => update({ zip: v })} placeholder="90210" testId="capture-zip" required />
          <Field label="CHOOSE A PASSWORD" type="password" name="new-password" id="capture-password" autoComplete="new-password" value={data.password} onChange={(v) => update({ password: v })} placeholder="Min 8 characters" testId="capture-password" required />

          {data.referralCode && (
            <div data-testid="referral-code-banner" style={{ marginTop: 20, padding: "12px 14px", background: referralStatus?.valid ? C.sage : C.bone, color: C.ink, fontSize: 13 }}>
              <strong>REFERRED BY:</strong> {data.referralCode}
              {referralStatus?.valid && (
                <div style={{ marginTop: 4, fontSize: 12, color: C.sageDark, fontWeight: 600 }}>
                  ✓ ${referralStatus.refereeDiscountAmount} off your first month — sent by {referralStatus.referrerFirstName}
                </div>
              )}
              {referralStatus && !referralStatus.valid && (
                <div style={{ marginTop: 4, fontSize: 12, color: C.coral }}>Code not valid — but you can still continue.</div>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, padding: "14px 16px", border: `1.5px dashed ${C.warmGray3}`, display: "flex", gap: 14, alignItems: "center", justifyContent: "center", minHeight: 70 }}>
            {TURNSTILE_SITE_KEY ? (
              <>
                <Turnstile
                  sitekey={TURNSTILE_SITE_KEY}
                  onVerify={(token) => update({ turnstileToken: token })}
                  onExpire={() => update({ turnstileToken: "" })}
                  onError={() => update({ turnstileToken: "" })}
                  theme="light"
                  size="flexible"
                />
                {!data.turnstileToken && (
                  <div style={{ fontSize: 12, color: C.warmGray, fontStyle: "italic" }}>Verifying you're human…</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: C.warmGray }}>Turnstile not configured.</div>
            )}
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: C.warmGray, letterSpacing: "0.1em" }} data-testid="turnstile-protected-msg">
            Protected by Cloudflare Turnstile
          </div>
        </div>

        <div style={{ marginTop: 32 }}>
          <Button variant="peach" size="lg" block testId="capture-continue-btn" onClick={submit}>
            CONTINUE →
          </Button>
        </div>
        <div style={{ marginTop: 16, fontSize: 12, color: "rgba(250,246,240,0.55)", textAlign: "center", lineHeight: 1.5 }}>
          By continuing, you agree to our <Link to="/legal/terms" style={{ color: C.peach }}>Terms</Link> and <Link to="/legal/privacy" style={{ color: C.peach }}>Privacy Policy</Link>. We'll never share your info or spam you.
        </div>
      </div>
    </div>
  );
}
